/**
 * Anchor (chapter_key) normalization, mirroring the server's chapterKeyFor().
 *
 * The server normalizes whatever it is sent, so without an identical client-side rule an
 * officer could type "Test Event 2026" and silently get "test-event-2026" stored — and
 * more importantly, the conflict check they were shown would have been performed against
 * the wrong string.
 */

/** Final, storable form. This is exactly what the server would derive. */
export const slugifyAnchor = (value: string): string =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * Same rules, but a trailing hyphen survives so "west-" can still be typed on the way to
 * "west-virtual". Stripped by slugifyAnchor() before the value is submitted.
 */
export const slugifyAnchorWhileTyping = (value: string): string =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+/, "");

/**
 * Validates an anchor against the anchors already used in the same year. Anchors are
 * unique per year across all chapters, so `taken` is year-scoped; pass the event's own
 * current anchor as `ignore` when editing so it doesn't collide with itself.
 *
 * Returns "" when there is nothing to report, including for an untouched empty field —
 * an empty form is incomplete, not wrong, and shouting at it on first render is noise.
 */
export const anchorError = (raw: string, year: number, taken: string[], ignore?: string): string => {
  if (!raw.trim()) return "";
  const slug = slugifyAnchor(raw);
  if (!slug) return "An anchor needs at least one letter or number.";
  const ignored = ignore ? slugifyAnchor(ignore) : "";
  if (slug !== ignored && taken.some((a) => slugifyAnchor(a) === slug)) {
    return `#${slug} is already used by another ${year} event.`;
  }
  return "";
};
